import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getOnsiteLiveAdminStatus } from '@/api/onsiteLiveAdmin';
import { listOnsiteLiveTeams } from '@/api/onsiteLiveTeams';
import { useAuth } from '@/auth/useAuth';
import { ErrorBanner } from '../BranchRelationships/shared';
import { DepartmentTeamsTab } from './DepartmentTeamsTab';
import { LimitsTab } from './LimitsTab';
import { RulePreviewTab } from './RulePreviewTab';
import { TEAMS_KEY } from './keys';
import { TeamsTab } from './TeamsTab';

type Tab = 'departments' | 'teams' | 'limits' | 'rule';

const tabs: { id: Tab; label: string }[] = [
  { id: 'departments', label: 'Departments → teams' },
  { id: 'teams', label: 'Teams' },
  { id: 'limits', label: 'Limits (DAL / PO)' },
  { id: 'rule', label: 'Rule preview' },
];

/**
 * Settings › Teams and limits — the delegated approval limits (DAL) that were only
 * ever edited straight in Onsitelive.DAL.* on SQ04 (plan D10, D13, D14).
 *
 * The chain is the same as for departments: job title → department (Position
 * Title Allocator) → team (drag here) → limits. The person's team is stored on
 * their OnsiteLive user, which OnFlow reads; the SQ06 job copies PO and credit
 * limits to Baseplan at 20:00 from the team Baseplan holds.
 *
 * Helpdesk reads. Admin edits: department mappings at any time (they change
 * nobody until the rule is applied); teams, limits and the apply only after the
 * OnsiteLive cutover, like the user screens.
 */
export function OnsiteLiveTeamsPage() {
  const { isAdmin } = useAuth();
  const [tab, setTab] = useState<Tab>('departments');

  const statusQuery = useQuery({ queryKey: ['onsitelive-admin', 'status'], queryFn: ({ signal }) => getOnsiteLiveAdminStatus(signal) });
  const teamsQuery = useQuery({ queryKey: TEAMS_KEY, queryFn: ({ signal }) => listOnsiteLiveTeams(signal) });

  const writesOpen = statusQuery.data?.WritesOpen === true;
  const canWrite = isAdmin && writesOpen;
  const teams = teamsQuery.data ?? [];

  return (
    <section className="max-w-7xl">
      <header className="mb-4">
        <h1 className="font-heading text-2xl font-bold text-navy">Teams and limits</h1>
        <p className="mt-1 text-sm text-darkGrey">
          Job title → department → team → approval limits. Drag a department onto a team; set each team&apos;s limits.
          OnFlow reads them at once; the Baseplan PO and credit limits are copied by the SQ06 job at 20:00.
        </p>
        {!isAdmin && <p className="mt-1 text-xs text-darkGrey/70">Read only — changes are made by an Oscar admin.</p>}
      </header>

      {statusQuery.isSuccess && !writesOpen && (
        <div role="status" className="mb-4 rounded border border-orange/40 bg-orange/5 px-3 py-2 text-sm text-darkGrey">
          <span className="font-semibold text-orange">Teams and limits are read only until the cutover. </span>
          Department mappings can be set now: they change nobody until the rule is applied.
        </div>
      )}
      {statusQuery.isError && <ErrorBanner message="Could not check whether editing is open, so teams and limits are read only." />}
      {teamsQuery.isError && <ErrorBanner message={(teamsQuery.error as Error).message || 'Could not load the teams.'} />}

      <div className="inline-flex flex-wrap rounded border border-border bg-white p-0.5 text-sm" role="tablist" aria-label="Teams and limits">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={tab === t.id}
            onClick={() => setTab(t.id)}
            className={tab === t.id ? 'rounded bg-navy px-3 py-1 font-medium text-white' : 'rounded px-3 py-1 font-medium text-darkGrey/70 hover:text-navy'}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="mt-4" role="tabpanel">
        {tab === 'departments' && <DepartmentTeamsTab teams={teams} canMap={isAdmin} />}
        {tab === 'teams' && <TeamsTab teams={teams} loading={teamsQuery.isLoading} canWrite={canWrite} />}
        {tab === 'limits' && <LimitsTab teams={teams} canWrite={canWrite} />}
        {tab === 'rule' && <RulePreviewTab canWrite={canWrite} />}
      </div>
    </section>
  );
}
